import {
  Button,
  Country,
  Flex,
  Header,
  InternationalPhoneData,
  InternationalPhoneInputField,
  OneColumnLayout,
  spaces,
  Text,
  TextInputField,
  Tooltip,
} from '@electricjs/arc';

import { Address, AddressGroupField } from '@/components/AddressGroupField';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useCreateShipmentMutation } from '@/redux/slices/shipmentApiSlice';
import { IT_HUB_ORIGIN } from '@/constants/common';
import SiteGroupField from './SiteGroupField';
import CountryMismatchBanner from './CountryMismatchBanner';
import { useGetEmployeeQuery } from '@/redux/slices/employeeApiSlice';
import { useSearchParams } from 'react-router-dom';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { getDisplayFullName } from 'common/utils/getFullName';
import { useGlobalUI } from '@/components/GlobalUIProvider';
import { useGetSiteByIdQuery } from '@/redux/slices/assetApiSlice';
import ShippingAddressDetails from './ShippingAddressDetails';
import {
  useGetRequestTypeCategoryChampionEmployee,
  RequestTypeCategoryNameEnum,
} from '@/hooks/useGetRequestTypeCategoryChampionEmployee';
import { useEffect, useMemo, useState } from 'react';
import { DeviceRetrieveAction } from '../types';
import {
  formatInternationalPhoneNumber,
  formatPhoneNumber,
} from 'common/utils/formatPhoneNumber';
import { SelectionParamKeys } from '@/types/queryParamKeys';
import {
  QRCodeLimitationsBanner,
  QRCodeHowItWorks,
} from '@/components/Shipments/QRCodeShippingInstructions';
import { ErrorPage } from '@/router/ErrorPage';
import { Asset } from '@/types/assets';

const GOOGLE_MAPS_API_KEY = window._env_?.VITE_GOOGLE_MAPS_PUB_API_KEY;

// The phone inputs emit { phoneNumber: E.164, country } objects. The
// automated US-to-US shipment workflow downstream expects the national
// "(555) 123-4567" format, so convert US numbers back; everything else goes
// to the manual support email, where E.164 is unambiguous.
const toSubmitPhone = (phone?: InternationalPhoneData): string | undefined =>
  phone?.country === 'US'
    ? formatPhoneNumber(phone.phoneNumber?.replace(/^\+1/, ''))
    : phone?.phoneNumber;

type DeviceRetrievalProps = {
  asset: Asset;
  onNext: () => void;
  onPrevious: () => void;
  isInternationalFlow: boolean;
  // Where the employee is located: their address country when on file,
  // otherwise the country the admin picked on the location-confirmation step.
  employeeCountry?: Country;
};

const DeviceShipping = ({
  asset,
  onNext,
  onPrevious,
  isInternationalFlow,
  employeeCountry,
}: DeviceRetrievalProps) => {
  const { t } = useTranslation('common');
  const { showSuccessToast, showErrorToast } = useGlobalUI();

  const { control, setValue, getValues, trigger } = useFormContext();
  const { isValid, errors } = useFormState();

  const [queryParams] = useSearchParams();
  const employeeId = queryParams.get(SelectionParamKeys.EmployeeId);
  const organizationId = useGetOrganizationId();

  const { data: employeeData, isLoading: isEmployeeLoading } =
    useGetEmployeeQuery(
      { employeeId: employeeId || '', organizationId },
      { skip: !employeeId }
    );
  const employeeName = getDisplayFullName(employeeData);
  const employeeEmail = employeeData?.email;
  const employeePersonalEmail = employeeData?.personalEmail;
  const employeeAddress = employeeData?.address;
  const employeePhone = employeeData?.phone;
  // The employee's address has no country when it isn't on file, so fall back
  // to the wizard-selected country for phone formatting.
  const employeePhoneCountry = employeeAddress?.country ?? employeeCountry;

  const receiverSite = useWatch({ name: 'receiverSite', defaultValue: {} });
  const senderAddress = useWatch({ name: 'senderAddress', control });
  const senderPhoneValue = useWatch({ name: 'senderPhone', control });
  const receiverPhoneValue = useWatch({ name: 'receiverPhone', control });
  const deviceRetrieveAction = useWatch({
    name: 'deviceRetrieveAction',
    control,
  });

  // With mode: 'onTouched', errors raised programmatically (trigger() on a
  // flow flip, setValue with shouldValidate) stick on fields the user hasn't
  // touched yet — RHF only revalidates untouched fields after a submit. These
  // custom (Controller-based) inputs don't mark themselves touched, so
  // revalidate them explicitly when their value changes while errored.
  useEffect(() => {
    if (errors.senderPhone) {
      trigger('senderPhone');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [senderPhoneValue]);
  useEffect(() => {
    if (errors.receiverPhone) {
      trigger('receiverPhone');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receiverPhoneValue]);
  useEffect(() => {
    if (errors.receiverSite) {
      trigger('receiverSite');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receiverSite]);
  const isShippingToWarehouse =
    deviceRetrieveAction === DeviceRetrieveAction.Warehouse;
  const isShippingToSite = deviceRetrieveAction === DeviceRetrieveAction.Ship;

  const { data: site, isFetching: isLoadingSite } = useGetSiteByIdQuery(
    {
      siteId: receiverSite?.value,
      organizationId: organizationId,
    },
    { skip: !receiverSite?.value || isShippingToWarehouse }
  );

  const receiverAddress = site?.address;

  // Only the ship-to-site path needs a country match: warehouse shipments
  // always go to the warehouse in the sender's own country (the fetched
  // warehouse site's address is the US warehouse's, which doesn't represent
  // the local warehouse actually receiving the device), so no check applies.
  const isCountryMismatch =
    isInternationalFlow &&
    isShippingToSite &&
    !!senderAddress?.country &&
    !!receiverAddress?.country &&
    senderAddress.country.toUpperCase() !==
      receiverAddress.country.toUpperCase();

  // The international flow always routes to the manual/support-email path on
  // the backend: no shipment or QR code is ever created, whether shipping to
  // a CA/PR site or to the warehouse in the employee's country.
  const isManualShipmentRequest = isInternationalFlow;

  // An international warehouse shipment goes to the warehouse in the sender's
  // own country, which the support team resolves when arranging the shipment.
  // The prefilled US warehouse's address and phone belong to a different
  // warehouse, so neither is shown or sent.
  const isInternationalWarehouseRequest =
    isManualShipmentRequest && isShippingToWarehouse;

  // Shipping to one of Electric's global warehouses can be done in almost any country, but
  // shipping to a company site is restricted to CA/PR (the only countries a
  // site can be eligible in for this flow). The warehouse case leaves the
  // autocomplete unrestricted: Google Places accepts at most 5 country codes
  // in its restriction, so an "every country but US" list would make every
  // search fail. The validation schema still rejects a US selection there.
  const senderAddressAllowedCountries = useMemo(() => {
    if (!isInternationalFlow) {
      return ['US'] as Country[];
    }
    return isShippingToSite ? (['CA', 'PR'] as Country[]) : undefined;
  }, [isInternationalFlow, isShippingToSite]);

  // Also disable while the selected site's data is loading: the ship-to-site
  // mismatch check can't run until the address is known (an already-valid
  // form would otherwise be briefly submittable to a mismatched-country
  // site), and the submit payload needs the site data either way. The site
  // query is skipped entirely for a warehouse shipment, so `isLoadingSite`
  // is never true in that case.
  const isNextDisabled =
    !isValid || isCountryMismatch || (isShippingToSite && isLoadingSite);

  const nextButtonDisabledReason = useMemo(() => {
    if (!isNextDisabled) {
      return undefined;
    }
    if (isCountryMismatch) {
      return t('offboardings.deviceShipping.countryMismatchBanner.title');
    }
    const missingFieldLabels = [
      ...new Set([
        errors.senderAddress && t('offboardings.deviceShipping.shippingFrom'),
        errors.senderPhone && t('offboardings.deviceShipping.phoneLabel'),
        errors.personalEmail &&
          t('offboardings.deviceShipping.personalEmailLabel'),
        errors.receiverSite && t('offboardings.deviceShipping.shippingTo'),
        errors.receiverPhone && t('offboardings.deviceShipping.phoneLabel'),
      ]),
    ].filter((label): label is string => !!label);

    if (missingFieldLabels.length === 0) {
      return t('offboardings.deviceShipping.nextButtonDisabledGeneric');
    }

    return t('offboardings.deviceShipping.nextButtonDisabledTooltip', {
      fields: missingFieldLabels.join(', '),
    });
  }, [isNextDisabled, isCountryMismatch, errors, t]);

  const hardwareChampion = useGetRequestTypeCategoryChampionEmployee(
    organizationId,
    RequestTypeCategoryNameEnum.DEVICE
  );

  const [phoneAutoFillKey, setPhoneAutoFillKey] = useState(0);
  const [receiverPhoneAutoFillKey, setReceiverPhoneAutoFillKey] = useState(0);

  const [createShipment, { isLoading: isCreatingShipment }] =
    useCreateShipmentMutation();

  useEffect(() => {
    if (
      deviceRetrieveAction === DeviceRetrieveAction.Ship ||
      deviceRetrieveAction === DeviceRetrieveAction.Warehouse
    ) {
      if (employeeAddress) {
        setValue('senderAddress', employeeAddress, { shouldValidate: true });
      }
      if (employeePhone) {
        setValue(
          'senderPhone',
          {
            // Strip display formatting down to E.164 so the auto-filled
            // value matches the shape the phone input emits on manual entry.
            phoneNumber: formatInternationalPhoneNumber(
              employeePhone,
              employeePhoneCountry
            ).replace(/[^+\d]/g, ''),
            country: employeePhoneCountry,
          } as InternationalPhoneData,
          { shouldValidate: true }
        );
        // Remount the phone input so it picks up the value set above
        // (it only reads its form value at mount).
        setPhoneAutoFillKey(key => key + 1);
      }
      if (employeePersonalEmail) {
        setValue('personalEmail', employeePersonalEmail, {
          shouldValidate: true,
        });
      }
    }
  }, [
    deviceRetrieveAction,
    employeeAddress,
    employeePhone,
    employeePhoneCountry,
    employeePersonalEmail,
    setValue,
  ]);

  useEffect(() => {
    if (site?.phone && !isInternationalWarehouseRequest) {
      setValue(
        'receiverPhone',
        {
          // Strip display formatting down to E.164 so the auto-filled
          // value matches the shape the phone input emits on manual entry.
          phoneNumber: formatInternationalPhoneNumber(
            site.phone,
            receiverAddress?.country
          ).replace(/[^+\d]/g, ''),
          country: receiverAddress?.country,
        } as InternationalPhoneData,
        { shouldValidate: true }
      );
      // Remount the phone input so it picks up the value set above
      // (it only reads its form value at mount).
      setReceiverPhoneAutoFillKey(key => key + 1);
    }
  }, [
    site?.phone,
    receiverAddress?.country,
    isInternationalWarehouseRequest,
    setValue,
  ]);

  const handleSubmit = async () => {
    const formData = getValues();
    const serialNumber = asset?.serialNumber;
    const deviceName = asset?.name || asset.device?.hardwareModel || 'Device';
    const administratorEmail = hardwareChampion?.email;
    const senderEmail = [
      ...new Set([formData.personalEmail, employeeEmail].filter(Boolean)),
    ].join(',');
    const senderPhone = toSubmitPhone(formData.senderPhone);
    // A warehouse shipment (domestic or international) has no real receiver
    // data to send — the backend resolves the warehouse's own site, address,
    // and phone via `shipToElectricWarehouse` instead.
    const receiverPhone = isShippingToWarehouse
      ? undefined
      : toSubmitPhone(formData.receiverPhone);

    const receiverShippingAddress = isShippingToWarehouse
      ? undefined
      : receiverAddress;

    if (
      !serialNumber ||
      !deviceName ||
      !formData.personalEmail ||
      !senderPhone ||
      !administratorEmail ||
      (!isShippingToWarehouse &&
        (receiverPhone === undefined || !receiverShippingAddress))
    ) {
      console.error('Missing required data for shipment creation');

      showErrorToast({
        id: 'shipment-error-toast',
        message: t('offboardings.deviceShipping.missingDataError'),
      });
      return;
    }

    const shipment = {
      senderName: employeeName,
      senderPhone,
      senderAddress: formData.senderAddress,
      deviceName,
      serialNumber,
      senderEmail,
      receiverName: isShippingToWarehouse ? undefined : receiverSite?.name,
      receiverPhone,
      receiverAddress: receiverShippingAddress,
      administratorEmail,
      shipmentOrigin: IT_HUB_ORIGIN,
      assetId: asset.id,
      destinationSiteId: isShippingToWarehouse
        ? undefined
        : receiverSite?.value,
      shipToElectricWarehouse: isShippingToWarehouse ? true : undefined,
    };

    try {
      await createShipment({
        organizationId,
        shipment,
      }).unwrap();

      showSuccessToast({
        id: 'shipment-success-toast',
        hideAfter: 5000,
        message: () => (
          <Text>
            <Trans
              i18nKey={
                isManualShipmentRequest
                  ? 'offboardings.deviceShipping.successToastNonUs'
                  : 'offboardings.deviceShipping.successToast'
              }
              ns="common"
              values={{ deviceName }}
              components={{ strong: <strong /> }}
            />
          </Text>
        ),
        stack: true,
      });
      onNext();
    } catch (error) {
      console.error('Error while creating shipment: ', error);
      showErrorToast({
        id: 'shipment-error-toast',
        message: t('offboardings.deviceShipping.errorToast'),
      });
    }
  };

  // Display the error page if there is no employee id (provided as a query param), if there is no employee data, or if there is no warehouse site after fetching the global sites
  if (!employeeId || (!isEmployeeLoading && !employeeData)) {
    return <ErrorPage />;
  }

  return (
    <OneColumnLayout>
      <Header
        title={t('offboardings.deviceShipping.title')}
        subtitle={t(
          isManualShipmentRequest
            ? 'offboardings.deviceShipping.subtitleManualRequest'
            : 'offboardings.deviceShipping.subtitle'
        )}
      />
      {/* A manual shipment request never creates a real shipment/QR code, so
          these would otherwise describe a QR code that will never arrive. */}
      {!isManualShipmentRequest && (
        <>
          <QRCodeHowItWorks
            listProps={{
              style: {
                marginLeft: '2rem',
                marginBottom: spaces.space_l_rem,
              },
            }}
          />
          <QRCodeLimitationsBanner
            bannerProps={{
              dismissible: true,
              isFullWidth: true,
            }}
          />
        </>
      )}
      <Flex vertical maxWidth="72rem">
        <Text variant="heading-3" mt="3.4rem" mb="2rem">
          {t('offboardings.deviceShipping.shippingFrom')}
        </Text>
        <ShippingAddressDetails
          id="sender-address-details"
          title={employeeName}
          address={employeeAddress}
          phone={
            isInternationalFlow
              ? formatInternationalPhoneNumber(
                  employeePhone,
                  employeePhoneCountry
                )
              : formatPhoneNumber(employeePhone)
          }
          isLoading={isEmployeeLoading}>
          <AddressGroupField
            name="senderAddress"
            control={control}
            allowedCountries={senderAddressAllowedCountries}
            onChange={(address: Address) => {
              setValue('senderAddress', address, {
                shouldValidate: true,
              });
            }}
            googleMapsApiKey={GOOGLE_MAPS_API_KEY}
            required
            columnGap="2rem"
          />
          <InternationalPhoneInputField
            // The arc component only reads its form value at mount, so
            // remount it after the employee's phone is auto-filled
            // asynchronously — otherwise the input stays blank.
            key={phoneAutoFillKey}
            name="senderPhone"
            control={control}
            label={t('offboardings.deviceShipping.phoneLabel')}
            required
          />
        </ShippingAddressDetails>
        <TextInputField
          id="personal-email-input"
          name="personalEmail"
          control={control}
          label={t('offboardings.deviceShipping.personalEmailLabel')}
          placeholder={t(
            'offboardings.deviceShipping.personalEmailPlaceholder'
          )}
          width="100%"
          required
        />
        <Text variant="heading-3" mt="3.4rem" mb="2rem">
          {t('offboardings.deviceShipping.shippingTo')}
        </Text>
        <ShippingAddressDetails
          id="receiver-address-details"
          title={
            isShippingToWarehouse
              ? t('offboardings.deviceShipping.electricWarehouseLabel')
              : receiverSite?.name
          }
          // The warehouse's address is hidden regardless of flow, both here
          // and when it's already known (e.g. while the site query is loading).
          address={isShippingToWarehouse ? undefined : receiverAddress}
          phone={site?.phone}
          isLoading={isLoadingSite}
          disabled={isShippingToWarehouse}
          showPhoneNumber={!isShippingToWarehouse}>
          <SiteGroupField
            name="receiverSite"
            required
            shouldShowAddress
            isInternationalFlow={isInternationalFlow}
          />
          {!isInternationalWarehouseRequest && !isShippingToWarehouse && (
            <InternationalPhoneInputField
              // The arc component only reads its form value at mount, so
              // remount it after the site's phone is auto-filled
              // asynchronously — otherwise the input stays blank.
              key={receiverPhoneAutoFillKey}
              name="receiverPhone"
              control={control}
              label={t('offboardings.deviceShipping.phoneLabel')}
              required
            />
          )}
        </ShippingAddressDetails>
        {isCountryMismatch && (
          <CountryMismatchBanner
            senderCountry={senderAddress.country.toUpperCase() as Country}
            siteCountry={receiverAddress?.country.toUpperCase() as Country}
          />
        )}
      </Flex>
      <Flex mt="9rem" hAlignContent="between" gap="3.4rem" width="100%">
        <Button
          id="devices-shipping-previous-button"
          variant="outline"
          onClick={onPrevious}
          disabled={isCreatingShipment}>
          {t('offboardings.deviceShipping.previousButton')}
        </Button>
        <Tooltip
          content={nextButtonDisabledReason}
          disabled={!isNextDisabled}
          hideWhenReferenceDisabled={false}
          placement="top">
          <Button
            id="devices-shipping-next-button"
            onClick={handleSubmit}
            loading={isCreatingShipment}
            disabled={isNextDisabled}>
            {t('offboardings.deviceShipping.nextButton')}
          </Button>
        </Tooltip>
      </Flex>
    </OneColumnLayout>
  );
};

export default DeviceShipping;
